import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  Drawer,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import { font } from "../../styles/font";
import { AddOutlined, CloseOutlined } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import CustomEditor from "../common/CustomEditor";
import { useEffect, useState } from "react";
import { useApplicationContext } from "../../context/ApplicationContext";
import useErrorHandler from "../../api/errorHandler/useErrorHandler";
import { useTranslation } from "react-i18next";
import { HttpStatusCode } from "axios";
import { updateDeleteTask_API } from "../../api/controller/TaskController";
import { Severity } from "../../constant/ApplicationVariables";

const TaskStatus = {
  open: "OPEN",
  close: "CLOSE",
};

export default function EditTaskComponent({ open, onClose, handleConfirm }) {
  const { activeProject, handleSnackbar, currentTaskData, setCurrentTaskData } =
    useApplicationContext();
  const theme = useTheme();
  const errorHandler = useErrorHandler();
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [taskData, setTaskData] = useState({
    title: "",
    description: "",
    status: "",
  });
  const [error, setError] = useState({});

  const handleClose = () => {
    setTaskData({ title: "", description: "", status: "" });
    setError({});
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleValidateInputs();
  };

  const handleValidateInputs = () => {
    let errorObj = {};

    if (!taskData?.title?.trim()) {
      errorObj.title = "Title is required";
    }

    if (!taskData?.description?.trim()) {
      errorObj.description = "Description is required";
    }

    if (!taskData?.status) {
      errorObj.status = "Status is required";
    }

    setError(errorObj);
    if (Object.keys(errorObj).length > 0) return;

    return handleContinue(taskData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTaskData({ ...taskData, [name]: value });

    // Clear error for this field when user starts typing
    if (error[name]) {
      setError({ ...error, [name]: "" });
    }
  };

  const handleContinue = async () => {
    try {
      setIsLoading(true);
      let body = {
        id: currentTaskData?.id,
        title: taskData?.title?.trim(),
        description: taskData?.description?.trim(),
        status: taskData?.status,
        projectName: activeProject?.name,
        lastModifiedAt: currentTaskData?.lastModifiedAt,
      };

      const response = await updateDeleteTask_API(body);

      if (response.status === HttpStatusCode.Ok) {
        handleSnackbar(
          response?.message || "Task updated successfully",
          Severity.success,
        );
        handleConfirm();
        handleClose();
      }

      if (response) {
        errorHandler(response, setIsLoading);
      }
    } catch (error) {
      console.log(error, "projectreponse errorr");
      handleSnackbar("Failed to update task", Severity.error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentTaskData) {
      setTaskData({
        title: currentTaskData?.title || "",
        description: currentTaskData?.description || "",
        status: currentTaskData?.status || "",
      });
    }
  }, [currentTaskData]);

  const statusOptions = [
    { label: "Open", value: TaskStatus.open },
    { label: "Closed", value: TaskStatus.close },
  ];

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      anchor="right"
      PaperProps={{
        sx: {
          width: 420,
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        className="flex flex-col h-full"
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            Edit Task
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main,
              },
            }}
          >
            <CloseOutlined />
          </IconButton>
        </Box>

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            p: 3,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {/* Title Field */}
          <FormControl fullWidth>
            <FormLabel
              sx={{
                mb: 1,
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontSize: "0.875rem",
              }}
            >
              Title *
            </FormLabel>
            <TextField
              name="title"
              placeholder="Enter task title"
              value={taskData?.title}
              onChange={handleChange}
              error={!!error?.title}
              helperText={error?.title}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.palette.background.default,
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                  },
                },
              }}
            />
          </FormControl>

          {/* Description Field */}
          <FormControl fullWidth>
            <FormLabel
              sx={{
                mb: 1,
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontSize: "0.875rem",
              }}
            >
              Description *
            </FormLabel>
            <TextField
              name="description"
              placeholder="Enter task description"
              value={taskData?.description}
              onChange={handleChange}
              error={!!error?.description}
              helperText={error?.description}
              multiline
              minRows={4}
              maxRows={8}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.palette.background.default,
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                  },
                },
              }}
            />
          </FormControl>

          {/* Status Field */}
          <FormControl fullWidth error={!!error?.status}>
            <FormLabel
              sx={{
                mb: 1,
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontSize: "0.875rem",
              }}
            >
              Status *
            </FormLabel>
            <Select
              name="status"
              value={taskData?.status || ""}
              onChange={handleChange}
              displayEmpty
              size="small"
              sx={{
                backgroundColor: theme.palette.background.default,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.divider,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.main,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2,
                },
              }}
            >
              <MenuItem value="" disabled>
                Select status
              </MenuItem>
              {statusOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    color:
                      option.value === TaskStatus.open
                        ? theme.palette.success.main
                        : option.value === TaskStatus.close
                          ? theme.palette.error.main
                          : theme.palette.text.primary,
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {error?.status && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.error.main,
                  mt: 0.5,
                  ml: 1.75,
                }}
              >
                {error.status}
              </Typography>
            )}
          </FormControl>

          {/* Project Info */}
          {activeProject && (
            <Box
              sx={{
                p: 2,
                borderRadius: theme.shape.borderRadius,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  display: "block",
                  mb: 0.5,
                }}
              >
                Project
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                }}
              >
                {activeProject?.name}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={handleClose}
            disabled={isLoading}
            sx={{
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,

              "&:hover": {
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },

              "&.Mui-disabled": {
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                opacity: 0.5,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={
              isLoading ||
              !taskData.title.trim() ||
              !taskData.description.trim() ||
              !taskData.status
            }
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.mode === "dark" ? "#000" : "#fff",
              fontWeight: 600,

              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },

              "&.Mui-disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }}
          >
            {isLoading ? "Updating..." : "Update Task"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

function MemberCard({ user }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        borderRadius: theme.shape.borderRadius,
        backgroundColor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.background.default, 0.5)
            : theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Checkbox />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          width: "100%",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {user?.name}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {user?.email}
        </Typography>
      </Box>
    </Box>
  );
}
